import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { HistoryDeliveryTableRow } from '@/components/history/HistoryDeliveryTableRow'
import { formatSwedishDay } from '@/lib/format'
import {
  addCancelLabel,
  addHandDelivery,
  canMarkLabelForRemoval,
  describeNextDelivery,
  nextFreeSequence,
  removeCancelLabel,
  removeHandDelivery,
  updateHandDelivery
} from '@/lib/history'
import type { DeliveryLine } from '@/components/history/HistoryDeliveryTableRow'
import type { HistoryHandDelivery, HistoryWorklistEntry, Shipment } from '@/api/types'

interface HistoryDeliveriesCardProps {
  shipments: Shipment[]
  entry: HistoryWorklistEntry
  isDisabled: boolean
  onChange: (entry: HistoryWorklistEntry) => void
}

function buildLines(shipments: Shipment[], entry: HistoryWorklistEntry): DeliveryLine[] {
  const labels = new Map(entry.cancelLabels.map((label) => [label.id, label]))
  const renumbers = new Map(entry.renumber.map((row) => [row.id, row]))
  const lines: DeliveryLine[] = []

  for (const shipment of shipments) {
    const label = labels.get(shipment.id)

    if (label) lines.push({ kind: 'label', sequence: shipment.sequence, shipment, label })
    else {
      const renumber = renumbers.get(shipment.id) ?? null
      const canRemove = canMarkLabelForRemoval(entry, shipment)

      lines.push({ kind: 'platform', sequence: renumber?.to ?? shipment.sequence, shipment, renumber, canRemove })
    }
  }
  entry.handDeliveries.forEach((delivery, index) => lines.push({ kind: 'hand', sequence: delivery.sequence, index, delivery }))

  return lines.sort((a, b) => a.sequence - b.sequence || (a.kind === 'label' ? 1 : 0) - (b.kind === 'label' ? 1 : 0))
}

export function HistoryDeliveriesCard({ shipments, entry, isDisabled, onChange }: HistoryDeliveriesCardProps): React.ReactElement {
  const lines = buildLines(shipments, entry)
  const hasLines = lines.length > 0

  const handleHandChange = (index: number, patch: Partial<HistoryHandDelivery>): void => {
    onChange(updateHandDelivery(entry, index, patch))
  }

  const handleHandRemove = (index: number): void => {
    onChange(removeHandDelivery(entry, index))
  }

  const handleLabelKeep = (id: number): void => {
    onChange(removeCancelLabel(entry, id))
  }

  const handleLabelRemove = (shipment: Shipment): void => {
    onChange(addCancelLabel(entry, shipment))
  }

  const handleAdd = (): void => {
    onChange(addHandDelivery(entry, nextFreeSequence(entry, shipments), formatSwedishDay(new Date())))
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>2. Leveranser</CardTitle>
        <CardDescription>
          Alla leveranser i nummerordning: de som finns i plattformen, de handskickade som läggs till och fraktsedlar som tas bort. Efteråt:{' '}
          {describeNextDelivery(entry)}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!hasLines && <p className="text-sm text-muted-foreground">Inga leveranser.</p>}
        {hasLines && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Vad händer</th>
                  <th className="px-3 py-2 font-medium">Nr</th>
                  <th className="px-3 py-2 font-medium">Dag</th>
                  <th className="px-3 py-2 font-medium">Status / innehåll</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <HistoryDeliveryTableRow
                    key={line.kind === 'hand' ? `hand-${line.index}` : `ship-${line.shipment.id}`}
                    line={line}
                    isDisabled={isDisabled}
                    onHandChange={handleHandChange}
                    onHandRemove={handleHandRemove}
                    onLabelKeep={handleLabelKeep}
                    onLabelRemove={handleLabelRemove}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          className="rounded-none"
          disabled={isDisabled}
          onClick={handleAdd}
        >
          Lägg till ett paket som skickades för hand
        </Button>
      </CardContent>
    </Card>
  )
}
